import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.js';
import axios from 'axios';
import { toast } from 'sonner';

const AdminPagos = () => {
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [pagos, setPagos] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [filtros, setFiltros] = useState({
    estado: 'todos',
    metodo_pago: 'todos',
    moneda: 'todos',
    fecha_desde: '',
    fecha_hasta: '',
    buscar: ''
  });

  // Estados para diálogos
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedPago, setSelectedPago] = useState(null);
  // Debug states removed
  
  // Estados para selección múltiple y eliminación
  const [selectedPagos, setSelectedPagos] = useState([]);
  const [eliminando, setEliminando] = useState(false);

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar todos los pagos/registraciones
      const pagosResponse = await axios.get(`${API}/admin/registraciones`);
      setPagos(pagosResponse.data.registraciones || []);

      // Cargar estadísticas de pagos
      const estadisticasResponse = await axios.get(`${API}/admin/registraciones/estadisticas`);
      setEstadisticas(estadisticasResponse.data || {});

    } catch (error) {
      toast.error('Error al cargar datos de pagos');
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency) => {
    const symbol = '$';
    const prefix = currency === 'USD' ? 'USD ' : 'UYU ';
    return `${prefix}${symbol}${(price || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSelectPago = (pagoId) => {
    setSelectedPagos(prev => {
      if (prev.includes(pagoId)) {
        return prev.filter(id => id !== pagoId);
      } else {
        return [...prev, pagoId];
      }
    });
  };

  const handleSelectAll = () => {
    const pagosFiltrados = aplicarFiltros();
    if (selectedPagos.length === pagosFiltrados.length) {
      setSelectedPagos([]);
    } else {
      setSelectedPagos(pagosFiltrados.map(p => p.id));
    }
  };

  const handleEliminarSeleccionados = async () => {
    if (selectedPagos.length === 0) {
      toast.error('No hay registros seleccionados');
      return;
    }

    const confirmMessage = selectedPagos.length === 1
      ? '¿Estás seguro de eliminar este registro de pago? Esta acción no se puede deshacer.'
      : `¿Estás seguro de eliminar ${selectedPagos.length} registros de pago? Esta acción no se puede deshacer.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setEliminando(true);
    let eliminados = 0;
    let errores = 0;

    try {
      for (const pagoId of selectedPagos) {
        try {
          await axios.delete(`${API}/admin/registraciones/${pagoId}`);
          eliminados++;
        } catch (error) {
          console.error(`Error eliminando ${pagoId}:`, error);
          errores++;
        }
      }

      if (eliminados > 0) {
        toast.success(`${eliminados} registro(s) eliminado(s) exitosamente`);
        setSelectedPagos([]);
        await loadData();
      }

      if (errores > 0) {
        toast.error(`${errores} registro(s) no pudieron ser eliminados`);
      }
    } catch (error) {
      console.error('Error en eliminación masiva:', error);
      toast.error('Error al eliminar registros');
    } finally {
      setEliminando(false);
    }
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      'pendiente': { color: 'bg-yellow-100 text-yellow-800', text: 'Pendiente', icon: '⏳' },
      'subido': { color: 'bg-blue-100 text-blue-800', text: 'Comprobante Subido', icon: '📄' },
      'aprobado': { color: 'bg-green-100 text-green-800', text: 'Aprobado', icon: '✅' },
      'rechazado': { color: 'bg-red-100 text-red-800', text: 'Rechazado', icon: '❌' },
      'pagado': { color: 'bg-green-100 text-green-800', text: 'Pagado', icon: '💳' },
      'cancelado': { color: 'bg-gray-100 text-gray-800', text: 'Cancelado', icon: '⊘' }
    };

    const config = statusConfig[estado] || statusConfig['pendiente'];
    return (
      <Badge className={config.color}>
        {config.icon} {config.text}
      </Badge>
    );
  };

  const getMetodoPagoBadge = (metodo) => {
    const methodConfig = {
      'tarjeta_credito_debito': { 
        color: 'bg-blue-100 text-blue-800', 
        text: 'Tarjeta de Crédito/Débito', 
        icon: '💳' 
      },
      'transferencia_bancaria': { 
        color: 'bg-purple-100 text-purple-800', 
        text: 'Transferencia Bancaria', 
        icon: '🏦' 
      },
      'efectivo': { 
        color: 'bg-green-100 text-green-800', 
        text: 'Efectivo', 
        icon: '💵' 
      },
      'estudiantes_becado': { 
        color: 'bg-yellow-100 text-yellow-800', 
        text: 'Estudiantes Becado', 
        icon: '🎓' 
      },
      // Backwards compatibility
      'tarjeta_credito': { 
        color: 'bg-blue-100 text-blue-800', 
        text: 'Tarjeta de Crédito/Débito', 
        icon: '💳' 
      },
      'contado': { 
        color: 'bg-green-100 text-green-800', 
        text: 'Efectivo', 
        icon: '💵' 
      },
      'transferencia': { 
        color: 'bg-purple-100 text-purple-800', 
        text: 'Transferencia Bancaria', 
        icon: '🏦' 
      },
      'stripe': { 
        color: 'bg-blue-100 text-blue-800', 
        text: 'Tarjeta de Crédito/Débito', 
        icon: '💳' 
      }
    };

    const config = methodConfig[metodo] || methodConfig['efectivo'];
    return (
      <Badge className={config.color}>
        {config.icon} {config.text}
      </Badge>
    );
  };

  const openDetailDialog = (pago) => {
    setSelectedPago(pago);
    setIsDetailDialogOpen(true);
  };

  // Functions moved to GestionBackupsFull.js

  const downloadReceipt = async (pagoId) => {
    try {
      const response = await axios.get(`${API}/admin/registraciones/${pagoId}/recibo`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recibo_${pagoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar recibo');
    }
  };

  const viewPaymentDocument = async (pagoId) => {
    try {
      const response = await axios.get(`${API}/admin/registracion/${pagoId}/comprobante`);
      const data = response.data;
      
      // Open document in new tab
      const documentUrl = `${API}${data.comprobante_url}`;
      window.open(documentUrl, '_blank');
      
      toast.success('Documento de pago abierto');
    } catch (error) {
      console.error('Error viewing payment document:', error);
      if (error.response?.status === 404) {
        toast.error('No hay documento de pago subido para esta registración');
      } else {
        toast.error('Error al abrir documento de pago');
      }
    }
  };

  const exportPayments = async () => {
    try {
      toast.info('Generando reporte de pagos...');
      // Aquí iría la lógica para generar un Excel con todos los pagos
      toast.success('Reporte generado exitosamente');
    } catch (error) {
      toast.error('Error al generar reporte');
    }
  };

  // Filtrar pagos
  const aplicarFiltros = () => {
    return pagos.filter(pago => {
      const matchesEstado = filtros.estado === 'todos' || pago.estado_pago === filtros.estado;
      const matchesMetodo = filtros.metodo_pago === 'todos' || pago.metodo_pago === filtros.metodo_pago;
      const matchesMoneda = filtros.moneda === 'todos' || pago.moneda === filtros.moneda;
      
      const matchesBuscar = !filtros.buscar || 
        pago.estudiante_nombre?.toLowerCase().includes(filtros.buscar.toLowerCase()) ||
        pago.estudiante_email?.toLowerCase().includes(filtros.buscar.toLowerCase()) ||
        pago.semestre_nombre?.toLowerCase().includes(filtros.buscar.toLowerCase());

      let matchesFecha = true;
      if (filtros.fecha_desde) {
        const fechaPago = new Date(pago.fecha_registro);
        const fechaDesde = new Date(filtros.fecha_desde);
        matchesFecha = fechaPago >= fechaDesde;
      }
      if (filtros.fecha_hasta && matchesFecha) {
        const fechaPago = new Date(pago.fecha_registro);
        const fechaHasta = new Date(filtros.fecha_hasta);
        matchesFecha = fechaPago <= fechaHasta;
      }

      return matchesEstado && matchesMetodo && matchesMoneda && matchesBuscar && matchesFecha;
    });
  };

  const pagosFiltrados = aplicarFiltros();

  if (user?.rol !== 'admin') {
    return (
      <div className="space-y-6">
        <Card className="shadow-md border-l-4 border-red-500">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Acceso Denegado</h3>
                <p className="text-gray-600">Solo los administradores pueden ver el panel de pagos.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Panel de Pagos</h1>
          <p className="text-gray-600">Monitorea todos los pagos de semestres y su estado</p>
          {selectedPagos.length > 0 && (
            <div className="mt-2 flex items-center gap-3">
              <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                {selectedPagos.length} seleccionado(s)
              </Badge>
              <Button
                onClick={handleEliminarSeleccionados}
                disabled={eliminando}
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                {eliminando ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Eliminando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash mr-2"></i>
                    Eliminar Seleccionados
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
        <Button
          onClick={exportPayments}
          variant="outline"
          className="instituto-gradient text-white"
        >
          <i className="fas fa-download mr-2"></i>
          Exportar Reporte
        </Button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Pagos</p>
                <p className="text-3xl font-bold text-blue-600">{estadisticas.total || 0}</p>
              </div>
              <i className="fas fa-receipt text-blue-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendientes</p>
                <p className="text-3xl font-bold text-yellow-600">{estadisticas.pendientes || 0}</p>
              </div>
              <i className="fas fa-clock text-yellow-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aprobados</p>
                <p className="text-3xl font-bold text-green-600">{estadisticas.aprobadas || 0}</p>
              </div>
              <i className="fas fa-check-circle text-green-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Ingresos</p>
                <p className="text-2xl font-bold text-indigo-600">${estadisticas.ingresos_total || 0}</p>
              </div>
              <i className="fas fa-dollar-sign text-indigo-600 text-3xl"></i>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center">
            <i className="fas fa-filter mr-3 text-blue-600"></i>
            Filtros de Búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="filter-estado">Estado</Label>
              <Select value={filtros.estado} onValueChange={(value) => setFiltros({...filtros, estado: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="subido">Comprobante Subido</SelectItem>
                  <SelectItem value="aprobado">Aprobado</SelectItem>
                  <SelectItem value="rechazado">Rechazado</SelectItem>
                  <SelectItem value="pagado">Pagado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter-metodo">Método</Label>
              <Select value={filtros.metodo_pago} onValueChange={(value) => setFiltros({...filtros, metodo_pago: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="tarjeta_credito_debito">Tarjeta de Crédito/Débito</SelectItem>
                  <SelectItem value="transferencia_bancaria">Transferencia Bancaria</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="estudiantes_becado">Estudiantes Becado</SelectItem>
                  {/* Backwards compatibility */}
                  <SelectItem value="tarjeta_credito">Tarjeta de Crédito (Legacy)</SelectItem>
                  <SelectItem value="contado">Contado (Legacy)</SelectItem>
                  <SelectItem value="transferencia">Transferencia (Legacy)</SelectItem>
                  <SelectItem value="stripe">Stripe (Legacy)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter-moneda">Moneda</Label>
              <Select value={filtros.moneda} onValueChange={(value) => setFiltros({...filtros, moneda: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="UYU">UYU</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="filter-desde">Desde</Label>
              <Input
                id="filter-desde"
                type="date"
                value={filtros.fecha_desde}
                onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="filter-hasta">Hasta</Label>
              <Input
                id="filter-hasta"
                type="date"
                value={filtros.fecha_hasta}
                onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="filter-buscar">Buscar</Label>
              <Input
                id="filter-buscar"
                placeholder="Nombre, email, semestre..."
                value={filtros.buscar}
                onChange={(e) => setFiltros({...filtros, buscar: e.target.value})}
              />
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pagosFiltrados.length > 0 && selectedPagos.length === pagosFiltrados.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Seleccionar todos
                </span>
              </label>
              <p className="text-sm text-gray-600">
                Mostrando {pagosFiltrados.length} de {pagos.length} pagos
              </p>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setFiltros({
                  estado: 'todos',
                  metodo_pago: 'todos',
                  moneda: 'todos',
                  fecha_desde: '',
                  fecha_hasta: '',
                  buscar: ''
                })}
              >
                <i className="fas fa-times mr-2"></i>
                Limpiar Filtros
              </Button>
              {/* Botones movidos a GestionBackupsFull.js */}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Pagos */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center">
            <i className="fas fa-list mr-3 text-green-600"></i>
            Pagos de Semestres ({pagosFiltrados.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pagosFiltrados.length === 0 ? (
            <div className="text-center py-8">
              <i className="fas fa-search text-gray-400 text-5xl mb-4"></i>
              <p className="text-gray-600">No se encontraron pagos con los filtros aplicados</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pagosFiltrados.map((pago) => (
                <Card key={pago.id} className={`border-l-4 border-l-blue-500 ${selectedPagos.includes(pago.id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <input
                            type="checkbox"
                            checked={selectedPagos.includes(pago.id)}
                            onChange={() => handleSelectPago(pago.id)}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          />
                          <h3 className="font-semibold text-lg">{pago.estudiante_nombre}</h3>
                          {getStatusBadge(pago.estado_pago)}
                          {getMetodoPagoBadge(pago.metodo_pago)}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Email:</span>
                            <p className="font-medium">{pago.estudiante_email}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Semestre:</span>
                            <p className="font-medium">{pago.semestre_nombre}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Monto:</span>
                            <p className="font-medium">{formatPrice(pago.monto, pago.moneda)}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Fecha:</span>
                            <p className="font-medium">{formatDate(pago.fecha_registro)}</p>
                          </div>
                          {pago.fecha_aprobacion && (
                            <div>
                              <span className="text-gray-600">Aprobado:</span>
                              <p className="font-medium">{formatDate(pago.fecha_aprobacion)}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => openDetailDialog(pago)}
                          variant="outline"
                          size="sm"
                        >
                          <i className="fas fa-eye mr-1"></i>
                          Ver Detalles
                        </Button>
                        
                        {pago.estado_pago === 'aprobado' && (
                          <Button
                            onClick={() => downloadReceipt(pago.id)}
                            variant="outline"
                            size="sm"
                            className="text-green-600"
                          >
                            <i className="fas fa-download mr-1"></i>
                            Recibo
                          </Button>
                        )}
                        
                        {(pago.estado_pago === 'subido' || pago.estado_pago === 'aprobado' || pago.estado_pago === 'rechazado') && (
                          <Button
                            onClick={() => viewPaymentDocument(pago.id)}
                            variant="outline"
                            size="sm"
                            className="text-blue-600"
                          >
                            <i className="fas fa-file-alt mr-1"></i>
                            Ver Comprobante
                          </Button>
                        )}
                        
                        {(pago.estado_pago === 'comprobante_subido' || pago.estado_pago === 'aprobado' || pago.estado_pago === 'rechazado') && (
                          <Button
                            onClick={() => viewPaymentDocument(pago.id)}
                            variant="outline"
                            size="sm"
                            className="text-blue-600"
                          >
                            <i className="fas fa-file-alt mr-1"></i>
                            Ver Comprobante
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Detalles del Pago</DialogTitle>
          </DialogHeader>
          
          {selectedPago && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Información del Estudiante</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Nombre:</strong> {selectedPago.estudiante_nombre}</p>
                    <p><strong>Email:</strong> {selectedPago.estudiante_email}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Información del Pago</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Estado:</strong> {getStatusBadge(selectedPago.estado_pago)}</p>
                    <p><strong>Monto:</strong> {formatPrice(selectedPago.monto, selectedPago.moneda)}</p>
                    <p><strong>Método:</strong> {getMetodoPagoBadge(selectedPago.metodo_pago)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Detalles del Semestre</h4>
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p><strong>Semestre:</strong> {selectedPago.semestre_nombre}</p>
                  <p><strong>Número:</strong> {selectedPago.semestre_numero}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Fechas</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><strong>Registro:</strong> {formatDate(selectedPago.fecha_registro)}</p>
                  {selectedPago.fecha_aprobacion && (
                    <p><strong>Aprobación:</strong> {formatDate(selectedPago.fecha_aprobacion)}</p>
                  )}
                </div>
              </div>

              {selectedPago.comentarios_revision && (
                <div>
                  <h4 className="font-semibold mb-2">Comentarios de Revisión</h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    {selectedPago.comentarios_revision}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailDialogOpen(false)}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modals moved to GestionBackupsFull.js */}
    </div>
  );
};

export default AdminPagos;